////////////////////////////////////////////////////////////////////////////////
//                                                                            //
//  IRONSIDE - Public Website GUI                                             //
//                                                                            //
//  Version: 1.0.6                                                            //
//  Summary: Career vacancy route — GET /public/careers/:slug               //
//                                                                            //
////////////////////////////////////////////////////////////////////////////////

import { Fragment } from 'react';
import { useParams, Link } from 'react-router';
import { AxiosError } from 'axios';

import { usePageTitle } from '@/hooks/usePageTitle';
import { useApi } from '@/hooks/useApi';
import { jobPostingJsonLd, useJsonLd } from '@/lib/seo';
import type { CareerDetailRecord } from '@/types/public';
import { Spinner } from '@/components/feedback/Spinner';
import { ErrorState } from '@/components/feedback/ErrorState';
import { Markdown } from '@/components/content/Markdown';
import { GlassTile } from '@/components/content/GlassTile';
import { PageHero } from '@/components/content/PageHero';
import { Reveal } from '@/components/content/Reveal';

// Key facts rendered inside the hero, under the title (Mike, 14/08/2026) —
// part of the "key info", sized up (~h5) versus the listing's small rows. The
// DB column is `discipline`; "Division" is its public-facing label.
function HeroFacts ({ vacancy }: { vacancy: CareerDetailRecord }) {
  const rows: [string, string][] = [];
  if (vacancy.discipline) rows.push(['Division', vacancy.discipline]);
  if (vacancy.location) rows.push(['Location', vacancy.location]);
  if (vacancy.employmentType) rows.push(['Type', vacancy.employmentType.replace(/_/g, ' ')]);
  if (vacancy.salary) rows.push(['Salary', vacancy.salary]);
  if (rows.length === 0) return null;
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-lg sm:text-xl">
      {rows.map(([label, value]) => (
        <Fragment key={label}>
          <dt className="font-medium text-muted">{label}:</dt>
          <dd className="text-ink">{value}</dd>
        </Fragment>
      ))}
    </dl>
  );
}

export function CareerDetail () {
  const { slug } = useParams<{ slug: string }>();
  usePageTitle(slug ?? 'Careers');

  const { status, data, error } = useApi<CareerDetailRecord>(slug ? `/public/careers/${slug}` : null);

  useJsonLd(status === 'success'
    ? jobPostingJsonLd({
        slug: data.slug,
        title: data.title,
        discipline: data.discipline,
        location: data.location,
        employmentType: data.employmentType,
        summary: data.summary,
        postedAt: data.postedAt,
        ...(data.closesAt ? { closesAt: data.closesAt } : {}),
        ...(data.salary ? { salary: data.salary } : {}),
      })
    : null);

  if (status === 'loading') {
    return (
      <div>
        <PageHero title="Careers" />
        <div className="mx-auto max-w-3xl px-6 py-16"><Spinner label="Loading vacancy" /></div>
      </div>
    );
  }

  if (status === 'error') {
    const notFound = error.cause instanceof AxiosError && error.cause.response?.status === 404;
    return (
      <div>
        <PageHero title="Careers" />
        <div className="mx-auto max-w-3xl px-6 py-16">
          <Reveal as="div">
            {notFound ? (
              <GlassTile role="alert" className="p-8 sm:p-12">
                <h2 className="text-3xl font-semibold text-ink">Vacancy not found</h2>
                <p className="mt-4 text-muted">This vacancy doesn't exist, has closed, or is no longer published.</p>
                <Link to="/careers" className="mt-4 inline-block underline">Back to Careers</Link>
              </GlassTile>
            ) : (
              <ErrorState message="This vacancy couldn't be loaded right now." />
            )}
          </Reveal>
        </div>
      </div>
    );
  }

  const vacancy = data;

  return (
    <div>
      <PageHero title={vacancy.title} subtitle={<HeroFacts vacancy={vacancy} />} />

      <Reveal as="article" className="mx-auto max-w-3xl px-6 py-16">
        <GlassTile className="p-8 sm:p-12">
          {vacancy.summary && <p className="text-lg text-muted">{vacancy.summary}</p>}

          {vacancy.bodyMd && <Markdown source={vacancy.bodyMd} className={vacancy.summary ? 'mt-6' : ''} />}

          {/* Application intake never happens in this webapp (resolved 09/08/2026)
              — Apply Now always links out to the external ATS. */}
          <a
            href={vacancy.applyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-8 inline-block rounded bg-ink px-6 py-3 font-medium text-paper transition-colors hover:bg-ink/90 focus:ring-2 focus:ring-ink/40 focus:outline-none"
          >
            Apply Now
          </a>
        </GlassTile>
      </Reveal>
    </div>
  );
}
